import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link as JoyLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useParams } from "react-router-dom";
import PageHeader from "../../../components/PageHeader";
import { useOrgSettingsContext } from "../../../providers/OrgSettingsProvider";
import { useOrganizationDashboardsApi } from "../api";
import { DashboardModuleDetailResponse } from "../types";
import { DashboardPanel } from "../components/DashboardPanel";

export const ModuleDashboardDetail: React.FC = () => {
  const { module_id = "" } = useParams();
  const { user } = useOrgSettingsContext();
  const organizationId = user.organization_id ?? "";
  const { getModuleDashboard } = useOrganizationDashboardsApi();
  const [result, setResult] = useState<DashboardModuleDetailResponse | null>(
    null,
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (!organizationId)
        throw new Error("Your organization could not be identified.");
      setResult(await getModuleDashboard(organizationId, module_id));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "This module dashboard could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, [getModuleDashboard, organizationId, module_id]);
  useEffect(() => {
    void load();
  }, [load]);
  if (loading)
    return (
      <Stack spacing={2} role="status" aria-label="Loading module dashboard">
        <Skeleton height={48} />
        <Skeleton height={220} />
      </Stack>
    );
  if (error)
    return (
      <Alert color="danger" role="alert">
        <Stack spacing={1}>
          <Typography>{error}</Typography>
          <Button startDecorator={<RefreshIcon />} onClick={() => void load()}>
            Retry
          </Button>
          <JoyLink
            component={RouterLink}
            to="/organization/settings/dashboards"
          >
            Back to module dashboards
          </JoyLink>
        </Stack>
      </Alert>
    );
  if (!result) return null;
  const panels = [...result.detail_panels].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  return (
    <>
      <PageHeader>
        <JoyLink
          component={RouterLink}
          to="/organization/settings/dashboards"
          startDecorator={<ArrowBackIcon />}
        >
          All module dashboards
        </JoyLink>
        <Typography level="h1">{result.display_name}</Typography>
        <Typography level="body-sm" color="neutral">
          Module dashboard
        </Typography>
      </PageHeader>
      {panels.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
            gap: 2,
          }}
        >
          {panels.map((panel) => (
            <DashboardPanel
              key={panel.panel_id}
              organizationId={organizationId}
              moduleId={result.module_id}
              level="detail"
              panel={panel}
            />
          ))}
        </Box>
      ) : (
        <Typography color="neutral">
          No dashboard panels are configured for this module.
        </Typography>
      )}
    </>
  );
};
